import React from 'react'

import { useSize } from '../../../hooks'
import { Check, ErrorOutline } from '../../../icons'
import { Button, ButtonProps } from '../../Button'
import { Link, type LinkProps } from '../../Link'
import { SnackbarVariant } from '../types'
import { Container, Content, Extra, IconContainer, Message } from './styles'

export interface SnackbarProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Snackbar ID (used internally with SnackbarProvider)
   */
  id?: number

  /**
   * Snackbar variant
   */
  variant?: SnackbarVariant

  /**
   * Duration in ms to close the snackbar
   * The snackbar will not close if duration is not set (default: null)
   */
  duration?: number

  /**
   * Callback triggered when snackbar is closed
   */
  onClose?: (id?: number) => void

  /**
   * Internal function to measure snackbar height
   */
  measure?: (args: { id: number; height: number }) => void

  /**
   * Content
   */
  children:
    | React.ReactNode
    | ((props: { onClose: () => void }) => React.ReactNode)

  /**
   * Extra information about the message
   */
  extra?:
    | React.ReactNode
    | ((props: { onClose: () => void }) => React.ReactNode)

  /**
   * Snackbar action
   */
  action?: {
    label: string
    href?: string
  } & Pick<LinkProps, 'as' | 'external' | 'target' | 'rel'> &
    Omit<ButtonProps, 'variant' | 'size'>
}

enum SnackbarStatus {
  BEFORE_OPEN = 'before_open',
  OPEN = 'open',
  CLOSING = 'closing',
}

/**
 * Snackbar component
 */
export const Snackbar: React.FC<SnackbarProps> = ({
  className,
  id,
  variant = 'default',
  duration = null,
  onClose,
  measure,
  children,
  extra,
  action,
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { offsetHeight } = useSize(ref)

  React.useEffect(() => {
    if (!id) return
    if (!measure) return
    if (!offsetHeight) return

    measure({ id, height: offsetHeight })
  }, [id, measure, offsetHeight])

  const [status, setStatus] = React.useState<SnackbarStatus>(
    !duration ? SnackbarStatus.OPEN : SnackbarStatus.BEFORE_OPEN,
  )

  React.useEffect(() => {
    setStatus(SnackbarStatus.OPEN)

    if (!duration) return

    const tId = setTimeout(() => {
      setStatus(SnackbarStatus.CLOSING)
    }, duration)

    return () => clearTimeout(tId)
  }, [duration])

  const handleClose = React.useCallback(() => {
    // Ensure the snackbar status is set to CLOSING
    if (status !== SnackbarStatus.CLOSING) return

    onClose?.(id)
  }, [status, onClose, id])

  const handleManualClose = React.useCallback(() => {
    setStatus(SnackbarStatus.CLOSING)
    handleClose()
  }, [handleClose])

  const handleActionClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      action?.onClick?.(e)

      handleManualClose()
    },
    [action, handleManualClose],
  )

  return (
    <Container
      ref={ref}
      className={className}
      variant={variant}
      isOpen={status === SnackbarStatus.OPEN}
      isClosing={status === SnackbarStatus.CLOSING}
      onTransitionEnd={handleClose}
    >
      {variant === 'success' && (
        <IconContainer>
          <Check />
        </IconContainer>
      )}
      {variant === 'error' && (
        <IconContainer>
          <ErrorOutline />
        </IconContainer>
      )}

      <Content>
        <Message>
          {typeof children === 'function'
            ? children({ onClose: handleManualClose })
            : children}
        </Message>

        {extra && (
          <Extra>
            {typeof extra === 'function'
              ? extra({ onClose: handleManualClose })
              : extra}
          </Extra>
        )}
      </Content>

      {action && (
        <div className="ml-4 tablet:ml-6">
          <Button
            {...action}
            variant="inverted"
            size="sm"
            onClick={handleActionClick}
            asChild={action.href ? true : undefined}
          >
            {action.href ? (
              <Link
                href={action.href}
                as={action.as}
                target={action.target}
                rel={action.rel}
                external={action.external}
              >
                {action.label}
              </Link>
            ) : (
              action.label
            )}
          </Button>
        </div>
      )}
    </Container>
  )
}
